<template>
  <div ref="galleryElement" class="gsc-gallery">
    <swiper-container
      ref="swiperElement"
      calculate-height="true"
      auto-height="true"
      navigation="true"
      observer="true"
      observe-parents="true"
    >
      <swiper-slide
        v-for="image in images"
        :key="image.src"
        lazy="true"
        @click="handleEnlargePhoto"
      >
        <img :src="image.src" :alt="image.alt" loading="lazy" class="-image" />
      </swiper-slide>
    </swiper-container>
    <div class="-options">
      <button
        class="-button --round --photo-wall"
        @click="handlePhotoWallClick"
      >
        <span class="icon-photo-wall"></span>
      </button>
      <button
        v-if="!locked"
        class="-button --round --cart"
        @click="handleAddToBasket"
      >
        <span class="icon-cart"></span>
      </button>
      <div class="-button">
        <div
          :data-smproduct="productId"
          style="z-index: 9999; padding-right: 10px; padding-top: 6px"
        >
          <div class="bhr-notification-center-like-button">
            <iframe
              :src="notificationCenterSrc"
              class="bhr-ncl"
              scrolling="no"
              style="border: 0px; display: block; height: 30px; width: 30px"
            ></iframe>
          </div>
        </div>
      </div>
      <button class="-button --round --enlarge" @click="handleEnlargePhoto">
        <span class="icon-enlarge"></span>
      </button>
    </div>
    <div v-if="showcasePhoto && showcaseEnabled" class="-showcase">
      <img class="-showcase__img" :src="showcasePhoto.originalSrc" />
      <div class="-showcase__options">
        <button
          class="-button --round --primary -showcase__new-tab"
          @click="handleShowcaseOpenInNewTab"
        >
          <Icon icon="open-external" />
        </button>
        <button
          class="-button --round --primary -showcase__close"
          @click="handleShowcaseClose"
        >
          <Icon icon="close" />
        </button>
      </div>
    </div>
  </div>
  <div class="gsc-gallery__nav">
    <swiper-container
      ref="swiperNavElement"
      navigation="true"
      slides-per-view="auto"
      space-between="10"
      free-mode="true"
    >
      <swiper-slide
        v-for="(image, index) in images"
        :key="image.src"
        :class="{
          '--active': currentSlide === index,
        }"
        @click="handleChangeSlide(index)"
      >
        <img :src="image.src" :alt="image.alt" loading="lazy" class="-image" />
      </swiper-slide>
    </swiper-container>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { register, SwiperContainer } from "swiper/element/bundle";

import Icon from "../icon/Icon.ce.vue";

import { GscGalleryImage } from "./types";
import { Swiper } from "swiper/types";
import { basketService } from "@/service/basket";
// import useSalesManago from "@/composable/useSalesmanago";

// const { boot: bootSm } = useSalesManago();

const props = defineProps<{
  productId: number | string;
  images: GscGalleryImage[] | String;
  photoWallUrl: string;
  locked?: boolean;
  showcaseEnabled?: boolean;
}>();

const galleryElement = ref<HTMLElement>();
const swiperElement = ref<SwiperContainer>();
const swiperNavElement = ref<SwiperContainer>();
const showcasePhoto = ref<GscGalleryImage>();
const currentSlide = ref<number>();

const images = computed<GscGalleryImage[]>(() =>
  typeof props.images === "string"
    ? JSON.parse(decodeURIComponent(props.images))
    : props.images
);

const notificationCenterSrc = computed(
  () =>
    `https://app2.salesmanago.pl/api/notification/center/like/icon.htm?vid=acacc08b5ac34cb7&centerId=278#productId=${props.productId}`
);

const handlePhotoWallClick = (): void => {
  window.open(props.photoWallUrl, "_blank");
};

const handleAddToBasket = async (): Promise<void> => {
  try {
    const res = await basketService.add(Number(props.productId), 1);

    document.dispatchEvent(
      new CustomEvent("gsc:added-to-basket", {
        detail: res,
      })
    );
  } catch (e) {
    alert(e as string); // TODO: some toast or other else
  }
};

const handleEnlargePhoto = (): void => {
  if (currentSlide.value === undefined) return;
  showcasePhoto.value = images.value[currentSlide.value];

  if (props.showcaseEnabled) {
    history.pushState({ showcase: true }, "", "#showcase");
  } else {
    window.location.href = showcasePhoto.value.originalSrc;
  }
};

const handleShowcaseClose = (): void => {
  showcasePhoto.value = undefined;
  history.state.showcase && history.back();
};

const handleShowcaseOpenInNewTab = (): void => {
  window.open(showcasePhoto.value?.src, "_blank");
};

const handleChangeSlide = (slide: number): void => {
  swiperElement.value?.swiper.slideTo(slide);
};

onMounted(() => {
  register();
  // bootSm(galleryElement.value as HTMLElement);

  currentSlide.value = swiperElement.value?.swiper.activeIndex;

  swiperElement.value?.swiper.on("slideChange", (swiper: Swiper) => {
    currentSlide.value = swiper.activeIndex;
    swiperNavElement.value?.swiper.slideTo(currentSlide.value);
  });

  window.addEventListener("keydown", (event: KeyboardEvent) => {
    event.key === "Escape" && handleShowcaseClose();
  });

  window.addEventListener("popstate", () => {
    handleShowcaseClose();
  });
});
</script>

<style lang="scss" scoped>
@use "sass:math";
@import "@/scss/_boilderplate.scss";
@import "@/scss/icons.scss";
@import "./style.scss";

.gsc-gallery {
  position: relative;

  .-image {
    width: 100%;
  }

  .-options {
    box-sizing: border-box;
    display: flex;
    flex-flow: row wrap;
    position: absolute;
    top: 0;
    left: 0;
    padding: $gutter;
    width: 100%;
    z-index: 1;
  }

  .-button {
    flex: 0 0 auto;
    width: $gallery-button-size;
    height: $gallery-button-size;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    line-height: 1;
    cursor: pointer;

    & + .-button {
      margin-left: #{math.div($gutter, 2)};
    }

    &.--round {
      background-color: rgba(white, 0.8);
      border-radius: 50%;
      transition: all 200ms ease-in-out;

      &:hover {
        background-color: white;
        transform: scale(1.1);
      }
    }

    &.--primary {
      background-color: #{color(primary)};
      color: white;

      &:hover {
        background-color: #{lighten(color(primary), 10)};
      }
    }

    &.--photo-wall {
      font-size: 2rem;
    }

    &.--cart {
      font-size: 1.75rem;

      & > .icon-cart {
        // TODO: sh*t icons
        transform: translateY(3px);
      }
    }

    &.--enlarge {
      position: absolute;
      right: $gutter;
      font-size: 1.25rem;
      justify-self: flex-end;

      & > .icon-enlarge {
        // TODO: sh*t icons
        transform: translateY(1px);
      }
    }
  }

  .-showcase {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    overscroll-behavior: contain;
    background-color: rgba(white, 0.97);
    backdrop-filter: blur(10px);
    z-index: 9999;

    &__img {
      max-width: 100%;
      max-height: 100%;
    }

    &__options {
      display: flex;
      flex-flow: row wrap;

      position: absolute;
      top: $gutter;
      right: $gutter;
    }
  }

  &__nav {
    padding-top: math.div($gutter, 2);
    swiper-slide {
      width: $gallery-navigation-element-size;
      height: $gallery-navigation-element-size;
      cursor: pointer;
      overflow: hidden;

      img {
        transition: transform 150ms linear;
        width: 100%;
        width: -webkit-fill-available;
        height: 100%;
        object-fit: cover;
      }

      &:hover {
        img {
          transform: scale(1.1);
        }
      }

      &.--active {
        &:before {
          content: "";
          position: absolute;
          display: block;
          width: 100%;
          height: 100%;
          box-sizing: border-box;
          border: #{math.div($gutter, 2)} solid #{color(primary)};
          z-index: 1;
        }
      }
    }
  }

  swiper-slide {
    font-size: 0;
    cursor: pointer;

    img {
      transition: all 250ms linear;
    }

    &:hover {
      img {
        transform: scale(1.1);
        filter: brightness(1.2);
      }
    }
  }
}
</style>
